import axios from 'axios';
import { authorizeRequest } from './authHeaders';

const captureApi = axios.create({ timeout: 120000 });

captureApi.interceptors.request.use(authorizeRequest);

export type ReplayStatus = 'queued' | 'building' | 'ready' | 'needs_fix' | 'failed';

export interface ReplaySummary {
  pass: boolean;
  reasons: string[];
  steps: number;
  stepsOk: number;
  median: number | null;
  worst: number | null;
  gaps: number;
  pageErrors: number;
  reached: number;
  buildMs: number;
}

export interface CaptureReplayFields {
  format: string | null;
  teamId: string | null;
  replayStatus: ReplayStatus | null;
  replayBuildId: string | null;
  replaySummary: ReplaySummary | null;
  replayError: string | null;
  replayAttempts: number;
  replayUpdatedAt: string | null;
  expiresAt: string | null;
}

export interface CaptureListItem extends CaptureReplayFields {
  id: string;
  name: string;
  url: string;
  status: 'PROCESSING' | 'READY' | 'FAILED' | 'ARCHIVED';
  frameCount: number | null;
  thumbnailUrl: string | null;
  createdAt: string;
  metadata: { viewport?: { width: number; height: number }; recordedAt?: string };
}

export interface CaptureFrame {
  index: number;
  screenshotUrl: string | null;
  elementsKey: string | null;
  trigger: { type?: string; title?: string | null; stepId?: string; cursor?: { x: number; y: number }; element?: { tag?: string; text?: string } } | null;
  notes: string | null;
  excluded: boolean;
}

export interface CaptureDetail extends CaptureReplayFields {
  id: string;
  name: string;
  url: string;
  status: string;
  frameCount: number | null;
  metadata: { viewport?: { width: number; height: number }; recordedAt?: string; pageTitle?: string; processingError?: string };
  frames: CaptureFrame[];
  /** The microphone and tab tracks (extension 1.4.0); absent when the capture has no audio. */
  audio?: { tracks: CaptureAudioTrack[] };
}

export type AudioSource = 'mic' | 'tab';

export interface CaptureAudioTrack {
  source: AudioSource;
  file: string;
  mime: string;
  durationS: number;
  sizeBytes: number;
  url: string;
}

/** transcript.json (packages/shared/src/capture/transcript.ts): step times are on the steps' clock, words on their track's. */
export interface CaptureTranscript {
  version: 1;
  captureId: string;
  tracks: Array<{ source: AudioSource; file: string; offset: number; words: Array<{ w: string; start: number; end: number }>; text: string }>;
  steps: Array<{ step: string; start: number | null; end: number | null; mic: string; tab: string }>;
}

export type CaptureTranscriptState =
  | { status: 'pending' | 'not_transcribed' }
  | { status: 'failed'; error: string }
  | { status: 'ready'; transcript: CaptureTranscript };

export interface CaptureReplay {
  replayStatus: ReplayStatus | null;
  replaySummary: ReplaySummary | null;
  replayError: string | null;
  buildId: string | null;
  viewerUrl: string | null;
  /** The server hosts replays at all (prod doesn't yet). */
  hosted: boolean;
  viewerUnavailable?: string;
}

export interface ReplayReportFrame {
  label: string;
  capture: string | null;
  replay: string | null;
  match: number | null;
}

export interface ReplayReportStep {
  id: string;
  kind: string;
  title: string;
  url: string | null;
  action: string;
  error: string | null;
  errorScreen?: string | null;
  frames: ReplayReportFrame[];
}

export interface ReplayReport {
  verdict: { pass: boolean; reasons: string[]; excused?: { id: string; title: string; match: number }[] };
  match: { frames: number; median: number | null; worst: number | null };
  stepsOk: number;
  stepsFailed: number;
  gaps: Record<string, number>;
  rows: ReplayReportStep[];
}

export type AppStatus = 'publishing' | 'ready' | 'failed';

export interface App {
  id: string;
  name: string;
  status: AppStatus;
  error: string | null;
  captureId: string | null;
  buildId: string;
  teamId: string | null;
  startPath: string | null;
  mapKey: string | null;
  url: string | null;
  mapUrl: string | null;
  createdAt: string;
}

export interface AppDetail extends App {
  map: Record<string, unknown> | null;
}

const APP_POLL_MS = 2000;
const APP_WAIT_MS = 10 * 60 * 1000;

export function apiErrorMessage(e: unknown): string {
  const body = (e as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  if (typeof body === 'string') return body;
  const message = (body as { message?: unknown } | undefined)?.message;
  if (typeof message === 'string') return message;
  return e instanceof Error ? e.message : 'Something went wrong.';
}

export const captureService = {
  list: async (limit = 20, offset = 0) => {
    const res = await captureApi.get<{ captures: CaptureListItem[]; total: number }>('/captures', { params: { limit, offset } });
    return res.data;
  },

  get: async (id: string) => {
    const res = await captureApi.get<CaptureDetail>(`/captures/${id}`);
    return res.data;
  },

  getReplay: async (id: string) => {
    const res = await captureApi.get<CaptureReplay>(`/captures/${id}/replay`);
    return res.data;
  },

  getReplayReport: async (id: string) => {
    const res = await captureApi.get<ReplayReport>(`/captures/${id}/replay/report`);
    return res.data;
  },

  getTranscript: async (id: string) => {
    const res = await captureApi.get<CaptureTranscriptState>(`/captures/${id}/transcript`);
    return res.data;
  },

  retryTranscript: async (id: string) => {
    const res = await captureApi.post<CaptureTranscriptState>(`/captures/${id}/transcribe`);
    return res.data;
  },

  getFrameElements: async (captureId: string, frameIndex: number) => {
    const res = await captureApi.get<unknown[]>(`/captures/${captureId}/frames/${frameIndex}/elements`);
    return res.data;
  },

  update: async (id: string, data: { name?: string; frames?: { index: number; notes?: string; excluded?: boolean }[] }) => {
    await captureApi.patch(`/captures/${id}`, data);
  },

  delete: async (id: string) => {
    await captureApi.delete(`/captures/${id}`);
  },

  importToProject: async (id: string, data: { projectId?: string; projectName?: string; frameIndices?: number[] }) => {
    const res = await captureApi.post<{ projectId: string; framesImported: number; briefPath: string }>(`/captures/${id}/import`, data);
    return res.data;
  },

  /** anyway: publish a replay whose check said it needs a fix (the API refuses it otherwise, 409 REPLAY_NEEDS_FIX). */
  publishApp: async (captureId: string, opts?: { anyway?: boolean }) => {
    const res = await captureApi.post<App>(`/captures/${captureId}/publish-app`, opts?.anyway ? { anyway: true } : undefined);
    return res.data;
  },

  publishAppAndWait: async (captureId: string, signal?: AbortSignal, opts?: { anyway?: boolean }): Promise<App> => {
    const deadline = Date.now() + APP_WAIT_MS;
    let app: App = await captureService.publishApp(captureId, opts);
    while (app.status === 'publishing') {
      if (Date.now() > deadline) throw new Error('The app is still publishing after 10 minutes. Try again in a while.');
      await new Promise(resolve => setTimeout(resolve, APP_POLL_MS));
      signal?.throwIfAborted();
      app = await captureService.getApp(app.id);
    }
    if (app.status === 'failed') throw new Error(app.error || 'The app could not be published.');
    return app;
  },

  listApps: async (status?: AppStatus) => {
    const res = await captureApi.get<{ apps: App[] }>('/apps', { params: status ? { status } : undefined });
    return res.data.apps;
  },

  getApp: async (id: string) => {
    const res = await captureApi.get<AppDetail>(`/apps/${id}`);
    return res.data;
  },

  listProjectApps: async (projectId: string) => {
    const res = await captureApi.get<{ apps: App[] }>(`/projects/${projectId}/apps`);
    return res.data.apps;
  },

  attachApp: async (projectId: string, appId: string) => {
    const res = await captureApi.post<App>(`/projects/${projectId}/apps`, { appId });
    return res.data;
  },

  detachApp: async (projectId: string, appId: string) => {
    await captureApi.delete(`/projects/${projectId}/apps/${appId}`);
  },
};
